// Diff STRUCTUREL de premier niveau entre deux documents (miroir client du résumé structuré
// que le backend écrit dans l'audit) : champs scalaires avec avant/après, tableaux de composants
// diffés par identité (`id` quand il existe, sinon libellé+index), objets imbriqués signalés
// sans plonger. Pur et sans React : la vue « Par blocs » du ChangeDiffViewer le rend.

export type ObjectChange =
	| { kind: "scalar"; field: string; op: "added" | "removed" | "modified"; before?: string; after?: string }
	| { kind: "block"; field: string; block: string; op: "added" | "removed" | "modified" | "moved" }
	| { kind: "object"; field: string; op: "added" | "removed" | "modified" };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isScalar = (value: unknown): boolean =>
	value === null || ["string", "number", "boolean"].includes(typeof value);

const renderScalar = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/** Libellé humain d'un bloc : son type de composant, sinon son titre, sinon sa valeur. */
const blockLabel = (item: unknown): string => {
	if (isPlainObject(item)) {
		for (const key of ["type", "component", "componentType", "key", "title", "name"]) {
			const value = item[key];
			if (typeof value === "string" && value.trim().length > 0) return value;
		}
		return "élément";
	}
	return isScalar(item) ? renderScalar(item) : "élément";
};

const deepEquals = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** Indexe un tableau par identité stable : `id:` si présent, sinon libellé#index. */
const indexItems = (array: unknown[]): Map<string, { item: unknown; index: number }> => {
	const map = new Map<string, { item: unknown; index: number }>();
	array.forEach((item, index) => {
		const id = isPlainObject(item) && typeof item.id === "string" && item.id.trim() ? item.id : null;
		let key = id ? `id:${id}` : `${blockLabel(item)}#${index}`;
		if (map.has(key)) key = `${key}#${index}`;
		map.set(key, { item, index });
	});
	return map;
};

const diffArray = (field: string, before: unknown[], after: unknown[]): ObjectChange[] => {
	const changes: ObjectChange[] = [];
	const oldItems = indexItems(before);
	const newItems = indexItems(after);

	newItems.forEach((entry, key) => {
		const old = oldItems.get(key);
		if (!old) {
			changes.push({ kind: "block", field, block: blockLabel(entry.item), op: "added" });
		} else if (!deepEquals(old.item, entry.item)) {
			changes.push({ kind: "block", field, block: blockLabel(entry.item), op: "modified" });
		} else if (old.index !== entry.index) {
			changes.push({ kind: "block", field, block: blockLabel(entry.item), op: "moved" });
		}
	});
	oldItems.forEach((entry, key) => {
		if (!newItems.has(key)) {
			changes.push({ kind: "block", field, block: blockLabel(entry.item), op: "removed" });
		}
	});
	return changes;
};

const describeField = (field: string, before: unknown, after: unknown, op: "added" | "removed" | "modified"): ObjectChange[] => {
	if (Array.isArray(before) || Array.isArray(after)) {
		return diffArray(field, Array.isArray(before) ? before : [], Array.isArray(after) ? after : []);
	}
	if (isScalar(before) || isScalar(after) || before === undefined || after === undefined) {
		const entry: ObjectChange = { kind: "scalar", field, op };
		if (before !== undefined && isScalar(before)) entry.before = renderScalar(before);
		if (after !== undefined && isScalar(after)) entry.after = renderScalar(after);
		if (entry.before === undefined && entry.after === undefined) {
			return [{ kind: "object", field, op }];
		}
		return [entry];
	}
	return [{ kind: "object", field, op }];
};

/**
 * Diff de premier niveau entre deux documents : une entrée par champ ajouté/retiré/modifié,
 * les tableaux détaillés bloc par bloc. Tableau vide quand rien n'a changé.
 */
export const diffObjects = (beforeDoc: unknown, afterDoc: unknown): ObjectChange[] => {
	const before = isPlainObject(beforeDoc) ? beforeDoc : {};
	const after = isPlainObject(afterDoc) ? afterDoc : {};
	const changes: ObjectChange[] = [];

	for (const field of Object.keys(after)) {
		if (!(field in before)) {
			changes.push(...describeField(field, undefined, after[field], "added"));
		} else if (!deepEquals(before[field], after[field])) {
			changes.push(...describeField(field, before[field], after[field], "modified"));
		}
	}
	for (const field of Object.keys(before)) {
		if (!(field in after)) {
			changes.push(...describeField(field, before[field], undefined, "removed"));
		}
	}
	return changes;
};
